'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useLocale } from './LocaleProvider';
import { TextReveal } from './primitives/TextReveal';
import { SophiaLogo } from './SophiaLogo';

/**
 * LiveDemo — "paste a product URL, see Sophia in action in 5 seconds".
 *
 * Hits the real /api/compare endpoint with a normalized payload derived
 * from either the pasted URL (if we recognize the retailer) or the user's
 * free-form text. The resulting modal renders with the same visual
 * language as the real extension's ResultsModal — proving the product
 * works without requiring an install.
 *
 * The "Try example" chips are pre-validated so demos always succeed
 * even if the user hasn't pasted anything.
 */

const API_BASE = 'https://api.mysophia.tech';

// Pre-wired examples hit real products in our seed data
const examples = [
  {
    label: 'Gucci GG Marmont',
    payload: {
      title: 'Gucci GG Marmont Small Shoulder Bag',
      price: 9400,
      currency: 'AED',
      url: 'https://www.farfetch.com/shopping/women/gucci-marmont-demo.aspx',
      retailerDomain: 'farfetch.com',
      brand: 'Gucci',
    },
  },
  {
    label: 'Hermès Birkin 25',
    payload: {
      title: 'Hermès Birkin 25 Togo Leather',
      price: 198000,
      currency: 'AED',
      url: 'https://theluxurycloset.com/demo-birkin-25',
      retailerDomain: 'theluxurycloset.com',
      brand: 'Hermès',
    },
  },
  {
    label: 'Chanel Classic Flap',
    payload: {
      title: 'Chanel Classic Flap Medium Caviar',
      price: 42000,
      currency: 'AED',
      url: 'https://www.mytheresa.com/demo-classic-flap.html',
      retailerDomain: 'mytheresa.com',
      brand: 'Chanel',
    },
  },
  {
    label: 'Rolex Submariner',
    payload: {
      title: 'Rolex Submariner Date 126610LN',
      price: 58000,
      currency: 'AED',
      url: 'https://www.farfetch.com/demo-sub.aspx',
      retailerDomain: 'farfetch.com',
      brand: 'Rolex',
    },
  },
];

interface Alt {
  retailerName: string;
  title: string;
  price: number;
  currency: string;
  affiliateUrl: string;
  matchScore: number;
}

interface Resale {
  estimated: number;
  confidence: 'high' | 'medium' | 'low';
  sampleSize: number;
}

interface Result {
  currentPrice: number;
  currency: string;
  alternatives: Alt[];
  resaleValue: Resale | null;
  savings: number;
  authenticityWarning: { level: 'warning' | 'caution'; message: string } | null;
  normalized: { brand: string; model: string };
}

export function LiveDemo() {
  const { locale } = useLocale();
  const isAr = locale === 'ar';

  const [selectedIdx, setSelectedIdx] = useState(0);
  const [result, setResult] = useState<Result | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const copy = isAr
    ? {
        eyebrow: 'جرّب قبل التثبيت',
        heading: 'تأكّد بنفسك.',
        sub: 'اختر قطعة — Sophia تقارن، تُقدّر، وتشير للأصالة في ثوانٍ.',
        button: 'قارن الآن',
        loading: 'نقارن 42 متجرًا…',
        retry: 'حاول مجددًا',
        currentPrice: 'السعر الحالي',
        betterPrice: 'أفضل سعر',
        resale: 'قيمة إعادة البيع',
        save: 'وفّر',
        empty: 'لا توجد بدائل أرخص الآن.',
        warning: 'تحذير أصالة',
      }
    : {
        eyebrow: 'Try before you install',
        heading: 'See it for yourself.',
        sub: 'Pick a piece — Sophia compares, estimates resale, and flags authenticity in seconds.',
        button: 'Run comparison',
        loading: 'Comparing 42 retailers…',
        retry: 'Try again',
        currentPrice: 'CURRENT',
        betterPrice: 'BETTER PRICE',
        resale: 'RESALE VALUE',
        save: 'Save',
        empty: 'No cheaper alternatives right now.',
        warning: 'AUTHENTICITY',
      };

  async function run() {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(examples[selectedIdx].payload),
      });
      const json = await res.json();
      if (!json.success) {
        throw new Error(json.error ?? 'Comparison failed');
      }
      setResult(json.data as Result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="relative py-12 sm:py-16 md:py-32 border-t border-white/5">
      <div className="mx-auto max-w-6xl px-5 sm:px-6">
        <div className="max-w-2xl mb-16">
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-gold-soft font-semibold mb-5">
            <span className="w-1 h-1 rounded-full bg-gold-soft" />
            {copy.eyebrow}
          </div>
          <TextReveal as="h2" className="font-serif text-display-sm text-cream leading-[0.98]">
            {copy.heading}
          </TextReveal>
          <p className="mt-6 text-fog-300 text-lg leading-relaxed max-w-xl">{copy.sub}</p>
        </div>

        <div className="grid lg:grid-cols-[1.05fr,1fr] gap-8 lg:gap-10 items-start">
          {/* Left: picker + CTA */}
          <div className="space-y-6">
            <div className="flex flex-wrap gap-2">
              {examples.map((ex, i) => (
                <button
                  key={ex.label}
                  onClick={() => {
                    setSelectedIdx(i);
                    setResult(null);
                    setError(null);
                  }}
                  className={`px-4 py-2 rounded-pill text-sm border transition ${
                    selectedIdx === i
                      ? 'bg-gold/10 border-gold/50 text-gold-soft'
                      : 'bg-white/[0.02] border-white/10 text-fog-300 hover:border-white/20'
                  }`}
                  dir="ltr"
                >
                  {ex.label}
                </button>
              ))}
            </div>

            <div className="rounded-luxe bg-obsidian-700 border border-white/10 p-5" dir="ltr">
              <div className="text-[10px] tracking-[0.2em] uppercase text-fog-500 mb-2">
                Selected piece
              </div>
              <div className="font-serif text-xl text-cream mb-1">
                {examples[selectedIdx].payload.title}
              </div>
              <div className="text-sm text-fog-300 mb-4">
                AED {examples[selectedIdx].payload.price.toLocaleString()} · on{' '}
                {examples[selectedIdx].payload.retailerDomain}
              </div>
              <button
                type="button"
                onClick={() => void run()}
                disabled={loading}
                className="px-6 py-3 rounded-pill bg-cta-gradient text-white font-semibold text-sm shadow-glow hover:bg-cta-gradient-hover transition disabled:opacity-60 disabled:cursor-wait"
              >
                {loading ? copy.loading : copy.button}
              </button>
            </div>

            {error && (
              <button
                onClick={() => void run()}
                className="text-sm text-rose hover:text-rose-soft transition"
              >
                {error} — {copy.retry}
              </button>
            )}
          </div>

          {/* Right: ResultsModal-style output */}
          <div className="relative min-h-[440px]" dir="ltr">
            <AnimatePresence mode="wait">
              {loading && (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 rounded-luxe bg-cream/5 border border-white/10 flex flex-col items-center justify-center"
                >
                  <div className="w-10 h-10 rounded-full border-2 border-gold/30 border-t-gold animate-spin mb-4" />
                  <div className="text-sm text-fog-300 tracking-wide">{copy.loading}</div>
                </motion.div>
              )}

              {result && !loading && (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, y: 20, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="rounded-luxe bg-cream text-obsidian-900 p-6 shadow-card-lg"
                >
                  <div className="flex items-center gap-2 mb-5">
                    <SophiaLogo size={28} className="rounded-md" />
                    <span className="font-semibold text-sm">Sophia</span>
                    {result.authenticityWarning && (
                      <span className="ml-auto text-[10px] tracking-widest text-rose font-bold px-2 py-1 bg-rose/10 rounded">
                        ⚠ {copy.warning}
                      </span>
                    )}
                  </div>

                  <div className="mb-5">
                    <div className="text-[10px] tracking-widest uppercase text-fog-700 mb-1">
                      {copy.currentPrice}
                    </div>
                    <div className="font-serif text-4xl font-bold">
                      {result.currency} {result.currentPrice.toLocaleString()}
                    </div>
                    <div className="text-xs text-fog-700 mt-1">
                      {result.normalized.brand} · {result.normalized.model}
                    </div>
                  </div>

                  {result.alternatives.length > 0 ? (
                    <div className="rounded-luxe bg-gradient-to-br from-violet-50 to-indigo-50 p-4 mb-4 border border-violet-100">
                      <div className="text-[10px] tracking-widest uppercase text-violet-800 font-semibold mb-2">
                        {copy.betterPrice}
                      </div>
                      <div className="space-y-2">
                        {result.alternatives.slice(0, 3).map((alt) => (
                          <motion.a
                            key={alt.affiliateUrl}
                            href={alt.affiliateUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{
                              duration: 0.4,
                              delay: 0.2 + result.alternatives.indexOf(alt) * 0.08,
                            }}
                            data-cursor="lens"
                            className="flex items-center justify-between p-2 rounded hover:bg-violet-100/60 transition"
                          >
                            <div>
                              <div className="text-xs text-obsidian-800 font-medium">
                                {alt.retailerName}
                              </div>
                              <div className="font-serif text-lg font-bold text-violet-900">
                                {alt.currency} {alt.price.toLocaleString()}
                              </div>
                            </div>
                            <div className="text-[10px] text-fog-700">↗</div>
                          </motion.a>
                        ))}
                      </div>
                      {result.savings > 0 && (
                        <div className="mt-3 pt-3 border-t border-violet-100 text-xs text-emerald font-semibold">
                          {copy.save} {result.currency}{' '}
                          {Math.round(result.savings).toLocaleString()}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-luxe bg-obsidian-900/5 p-4 mb-4 text-center text-sm text-fog-700">
                      {copy.empty}
                    </div>
                  )}

                  {result.resaleValue && (() => {
                    const market = result.resaleValue.estimated;
                    const diffPct = Math.round(((result.currentPrice - market) / market) * 100);
                    const positioning =
                      diffPct > 5  ? `${diffPct}% above market`
                      : diffPct < -5 ? `${Math.abs(diffPct)}% below market`
                      : 'at market';
                    const posColor = diffPct > 5 ? 'text-rose' : diffPct < -5 ? 'text-emerald' : 'text-gold-deep';
                    return (
                      <div className="rounded-luxe bg-gold/8 border border-gold/20 p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-[10px] tracking-widest uppercase text-gold-deep font-semibold">
                            {copy.resale}
                          </div>
                          <div className="text-[10px] font-mono text-gold-deep uppercase">
                            {result.resaleValue.confidence}
                            {result.resaleValue.sampleSize > 0 ? ` · ${result.resaleValue.sampleSize} comps` : ''}
                          </div>
                        </div>
                        <div className="font-serif text-xl font-bold text-obsidian-900">
                          {result.currency} {Math.round(market).toLocaleString()}
                        </div>
                        <div className={`text-xs font-semibold ${posColor} mt-0.5`}>
                          This listing is {positioning}
                        </div>
                      </div>
                    );
                  })()}
                </motion.div>
              )}

              {!loading && !result && (
                <motion.div
                  key="idle"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 rounded-luxe border border-dashed border-white/10 flex items-center justify-center"
                >
                  <div className="text-center">
                    <div className="mx-auto mb-3 flex justify-center"><SophiaLogo size={48} /></div>
                    <div className="text-sm text-fog-500 tracking-wide">
                      {copy.sub.split('—')[0]}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
